import { useState, useEffect, useRef } from 'react';
import logo from '../assets/logo_golden_white.png';
import {
  lookupByPhone,
  createFDVisitPublic,
  registerAndVisitPublic,
} from '../services/foodDistributionService';
import {
  FDClientLookupResult,
  FDCreateClientRequest,
  FDVisit,
  REFERRAL_TYPE_OPTIONS,
  TEFAP_ELIGIBILITY_OPTIONS,
} from '../types/foodDistribution';
import '../styles/KioskMode.css';
import '../styles/FoodCheckIn.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const USDA_HH_OPTIONS = ['Often true', 'Sometimes true', 'Never true', "Don't know"] as const;
const USDA_YES_NO_OPTIONS = ['Yes', 'No', "Don't know"] as const;
const DISTRIBUTION_TYPES = [
  { value: 'Pantry', label: 'Food Pantry' },
  { value: 'Food Co-op', label: 'Food Co-op' },
  { value: 'Other', label: 'Other' },
] as const;

type UsdaHhAnswer = (typeof USDA_HH_OPTIONS)[number] | '';
type UsdaYesNoAnswer = (typeof USDA_YES_NO_OPTIONS)[number] | '';

// Progress step definitions — step 2 shown only for new clients, step 6 shown only if score >= 3
const PROGRESS_STEPS = [
  { id: 1, label: 'Phone' },
  { id: 2, label: 'Info' }, // conditionally shown
  { id: 3, label: 'Visit' },
  { id: 4, label: 'Survey' },
  { id: 5, label: 'Produce' },
  { id: 6, label: 'Referral' }, // conditionally shown
  { id: 7, label: 'Done' },
];

// ─── USDA Raw Score ───────────────────────────────────────────────────────────

function computeUsdaRawScore(
  hh3: UsdaHhAnswer,
  hh4: UsdaHhAnswer,
  ad1: UsdaYesNoAnswer,
  ad1aDays: number,
  ad1aDontknow: boolean,
  ad2: UsdaYesNoAnswer,
  ad3: UsdaYesNoAnswer
): number {
  let score = 0;
  if (hh3 === 'Often true' || hh3 === 'Sometimes true') {
    score++;
  }
  if (hh4 === 'Often true' || hh4 === 'Sometimes true') {
    score++;
  }
  if (ad1 === 'Yes') {
    score++;
    if (!ad1aDontknow && ad1aDays >= 3) {
      score++;
    }
  }
  if (ad2 === 'Yes') {
    score++;
  }
  if (ad3 === 'Yes') {
    score++;
  }
  return score;
}

// ─── Phone Helpers ────────────────────────────────────────────────────────────

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length < 4) {
    return digits;
  }
  if (digits.length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

function normalizePhone(formatted: string): string {
  return formatted.replace(/\D/g, '');
}

// ─── Component ────────────────────────────────────────────────────────────────

export function FoodCheckInPage() {
  // Navigation
  const [currentStep, setCurrentStep] = useState(1);
  const [isNewClient, setIsNewClient] = useState(false);

  // Step 1 — phone lookup
  const [phoneDisplay, setPhoneDisplay] = useState('');
  const [lookupResults, setLookupResults] = useState<FDClientLookupResult[]>([]);
  const [lookupDone, setLookupDone] = useState(false);
  const [fdClientId, setFdClientId] = useState<number | null>(null);

  // Step 2 — intake form (new clients)
  const [intakeFirstName, setIntakeFirstName] = useState('');
  const [intakeLastName, setIntakeLastName] = useState('');
  const [intakeZip, setIntakeZip] = useState('');
  const [householdSize, setHouseholdSize] = useState(1);
  const [childrenCount, setChildrenCount] = useState(0);
  const [pendingNewClient, setPendingNewClient] = useState<FDCreateClientRequest | null>(null);

  // TEFAP eligibility
  const [tefapSelections, setTefapSelections] = useState<Set<string>>(new Set());
  const [needsTefap, setNeedsTefap] = useState(false);

  // Step 4 — USDA survey
  const [usdaHh3, setUsdaHh3] = useState<UsdaHhAnswer>('');
  const [usdaHh4, setUsdaHh4] = useState<UsdaHhAnswer>('');
  const [usdaAd1, setUsdaAd1] = useState<UsdaYesNoAnswer>('');
  const [usdaAd1aDays, setUsdaAd1aDays] = useState(0);
  const [usdaAd1aDontknow, setUsdaAd1aDontknow] = useState(false);
  const [usdaAd2, setUsdaAd2] = useState<UsdaYesNoAnswer>('');
  const [usdaAd3, setUsdaAd3] = useState<UsdaYesNoAnswer>('');

  // Step 5 — fruit/veg + distribution type
  const [fruitvegDays, setFruitvegDays] = useState(3);
  const [fruitvegDontknow, setFruitvegDontknow] = useState(false);
  const [distributionType, setDistributionType] = useState('');

  // Step 6 — referrals (conditional)
  const [selectedReferrals, setSelectedReferrals] = useState<Set<string>>(new Set());

  // Step 7 — confirmation
  const [visit, setVisit] = useState<FDVisit | null>(null);
  const [confirmedReferrals, setConfirmedReferrals] = useState<string[]>([]);
  const [countdown, setCountdown] = useState(30);

  // Duplicate override
  const [bypassDuplicateWarning, setBypassDuplicateWarning] = useState(false);

  // Shared state
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // ── Auto-reset timer on confirmation screen ──
  useEffect(() => {
    if (currentStep !== 7) {
      return;
    }
    setCountdown(30);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownRef.current!); // eslint-disable-line @typescript-eslint/no-non-null-assertion
          handleReset();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (countdownRef.current) {
        clearInterval(countdownRef.current);
      }
    };
  }, [currentStep]);

  // ── Reset all state ──
  function handleReset() {
    setCurrentStep(1);
    setIsNewClient(false);
    setPhoneDisplay('');
    setLookupResults([]);
    setLookupDone(false);
    setFdClientId(null);
    setIntakeFirstName('');
    setIntakeLastName('');
    setIntakeZip('');
    setHouseholdSize(1);
    setChildrenCount(0);
    setPendingNewClient(null);
    setTefapSelections(new Set());
    setNeedsTefap(false);
    setUsdaHh3('');
    setUsdaHh4('');
    setUsdaAd1('');
    setUsdaAd1aDays(0);
    setUsdaAd1aDontknow(false);
    setUsdaAd2('');
    setUsdaAd3('');
    setFruitvegDays(5);
    setFruitvegDontknow(false);
    setDistributionType('');
    setSelectedReferrals(new Set());
    setVisit(null);
    setConfirmedReferrals([]);
    setBypassDuplicateWarning(false);
    setErrorMsg('');
  }

  // ── Step 1: Phone lookup ──
  async function handlePhoneLookup() {
    const phone = normalizePhone(phoneDisplay);
    if (phone.length < 10) {
      setErrorMsg('Please enter a valid 10-digit phone number.');
      return;
    }
    setIsLoading(true);
    setErrorMsg('');
    try {
      const results = await lookupByPhone(phone);
      setLookupResults(results);
      setLookupDone(true);
      if (results.length === 0) {
        setIsNewClient(true);
        setCurrentStep(2);
      }
      // 1 result → show confirmation card inline (handled by render)
      // 2+ results → show name list inline (handled by render)
    } catch {
      setErrorMsg('Something went wrong. Please try again or ask a staff member for help.');
    } finally {
      setIsLoading(false);
    }
  }

  // ── Step 2: Validate & store new-client data (no API call yet) ──
  function handleIntakeSubmit() {
    if (!intakeFirstName.trim() || !intakeLastName.trim() || !intakeZip.trim()) {
      setErrorMsg('Please fill in all required fields.');
      return;
    }
    const zipDigits = intakeZip.replace(/\D/g, '');
    if (zipDigits.length !== 5) {
      setErrorMsg('Please enter a valid 5-digit zip code.');
      return;
    }
    if (tefapSelections.size === 0) {
      setErrorMsg('Please select at least one TEFAP eligibility category.');
      return;
    }
    setErrorMsg('');
    setPendingNewClient({
      firstName: intakeFirstName.trim(),
      lastName: intakeLastName.trim(),
      phone: normalizePhone(phoneDisplay),
      zipCode: zipDigits,
      householdSize,
      childrenInHousehold: childrenCount,
      tefapEligibility: [...tefapSelections],
    });
    setCurrentStep(3);
  }

  // ── Step 5 → 6 or submit ──
  function handleAfterStep5() {
    setErrorMsg('');
    const score = computeUsdaRawScore(
      usdaHh3,
      usdaHh4,
      usdaAd1,
      usdaAd1aDays,
      usdaAd1aDontknow,
      usdaAd2,
      usdaAd3
    );
    if (score >= 3) {
      setCurrentStep(6);
    } else {
      submitVisit([]);
    }
  }

  // ── Submit visit ──
  async function submitVisit(referralTypes: string[]) {
    setIsLoading(true);
    setErrorMsg('');

    const visitData = {
      distributionType,
      usdaHh3: usdaHh3 || undefined,
      usdaHh4: usdaHh4 || undefined,
      usdaAd1: usdaAd1 || undefined,
      usdaAd1a: usdaAd1 === 'Yes' && !usdaAd1aDontknow ? usdaAd1aDays : null,
      usdaAd1aDontknow: usdaAd1 === 'Yes' ? usdaAd1aDontknow : undefined,
      usdaAd2: usdaAd2 || undefined,
      usdaAd3: usdaAd3 || undefined,
      fruitvegDays: fruitvegDontknow ? null : fruitvegDays,
      fruitvegDontknow,
      referralTypes: referralTypes.length > 0 ? referralTypes : undefined,
      tefapEligibility: tefapSelections.size > 0 ? [...tefapSelections] : undefined,
      bypassDuplicateWarning: bypassDuplicateWarning || undefined,
    };

    try {
      if (pendingNewClient) {
        // New client — atomically register + record visit in one request
        const result = await registerAndVisitPublic({
          ...pendingNewClient,
          ...visitData,
        });
        setVisit(result.visit);
        setConfirmedReferrals(referralTypes);
      } else if (fdClientId) {
        // Existing client — visit only
        const result = await createFDVisitPublic({
          fdClientId,
          ...visitData,
        });
        setVisit(result);
        setConfirmedReferrals(referralTypes);
      }
      setCurrentStep(7);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : '';
      if (msg.toLowerCase().includes('already checked in today')) {
        setErrorMsg(msg);
      } else {
        setErrorMsg(
          'Could not record your visit. Please try again or ask a staff member for help.'
        );
      }
    } finally {
      setIsLoading(false);
    }
  }

  // ── Progress indicator ──
  function renderProgress() {
    // Determine which steps are visible given context
    const stepsToShow = PROGRESS_STEPS.filter((s) => {
      if (s.id === 2 && !isNewClient) {
        return false;
      }
      if (s.id === 6) {
        return false;
      } // always hidden until we know score — treat survey+referral as one segment
      if (s.id === 7) {
        return false;
      } // confirmation replaces progress bar
      return true;
    });

    return (
      <div className="fd-progress" role="progressbar" aria-label="Check-in progress">
        {stepsToShow.map((s) => {
          const isActive = currentStep === s.id || (s.id === 5 && currentStep === 6);
          const isCompleted = currentStep > s.id;
          return (
            <div
              key={s.id}
              className={`fd-progress-dot ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
              aria-label={s.label}
              title={s.label}
            />
          );
        })}
      </div>
    );
  }

  // ── Shared UI helpers ──
  function renderError() {
    if (!errorMsg) {
      return null;
    }
    return (
      <div className="kiosk-alert kiosk-alert-error" role="alert">
        {errorMsg}
      </div>
    );
  }

  function renderLoading() {
    return (
      <div className="kiosk-loading">
        <div className="kiosk-spinner" aria-label="Loading" />
        <p>Please wait...</p>
      </div>
    );
  }

  // ─── Step 1: Phone Lookup ─────────────────────────────────────────────────

  function renderStep1() {
    // Single match → welcome-back confirmation
    if (lookupDone && lookupResults.length === 1) {
      const match = lookupResults[0];
      return (
        <div className="kiosk-form-card">
          <h2 className="kiosk-section-title">Welcome back!</h2>
          <p className="kiosk-section-description">Is this you?</p>
          <div className="fd-client-list">
            <div
              className="fd-client-item"
              style={{
                cursor: 'default',
                justifyContent: 'center',
                fontSize: '1.5rem',
                fontWeight: 700,
              }}
            >
              <span className="fd-client-icon">👤</span>
              {match.firstName} {match.lastInitial}.
            </div>
          </div>
          {match.checkedInToday && (
            <div
              className="kiosk-alert kiosk-alert-warning"
              role="alert"
              style={{ marginTop: '1rem' }}
            >
              <strong>{match.firstName} has already checked in today.</strong>
              <br />
              If this is a mistake, please see a staff member. To continue anyway, tap
              &quot;Continue Check-In Anyway&quot; below.
            </div>
          )}
          {renderError()}
          <div className="fd-confirm-buttons">
            <button
              className="kiosk-button kiosk-button-primary"
              onClick={() => {
                setFdClientId(match.fdClientId);
                if (match.checkedInToday) {
                  setBypassDuplicateWarning(true);
                }
                if (!match.hasTefapEligibility) {
                  setNeedsTefap(true);
                }
                setCurrentStep(3);
              }}
            >
              {match.checkedInToday ? 'Continue Check-In Anyway' : "Yes, that's me"}
            </button>
            <button
              className="kiosk-button kiosk-button-secondary"
              onClick={() => {
                setLookupDone(false);
                setLookupResults([]);
                setIsNewClient(true);
                setCurrentStep(2);
              }}
            >
              Not me — register me
            </button>
          </div>
        </div>
      );
    }

    // Multiple matches → name selection list
    if (lookupDone && lookupResults.length > 1) {
      return (
        <div className="kiosk-form-card">
          <h2 className="kiosk-section-title">Select your name</h2>
          <p className="kiosk-section-description">
            Multiple accounts found. Please tap your name:
          </p>
          {renderError()}
          <div className="fd-client-list">
            {lookupResults.map((r) => (
              <button
                key={r.fdClientId}
                className="fd-client-item"
                onClick={() => {
                  setFdClientId(r.fdClientId);
                  if (r.checkedInToday) {
                    setBypassDuplicateWarning(true);
                  }
                  if (!r.hasTefapEligibility) {
                    setNeedsTefap(true);
                  }
                  setCurrentStep(3);
                }}
              >
                <span className="fd-client-icon">👤</span>
                {r.firstName} {r.lastInitial}.
                {r.checkedInToday && (
                  <span
                    style={{
                      marginLeft: '0.5rem',
                      fontSize: '0.75rem',
                      color: '#b45309',
                      fontWeight: 600,
                    }}
                  >
                    Already checked in today
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="kiosk-nav-buttons" style={{ marginTop: '1rem' }}>
            <button
              className="kiosk-button kiosk-button-secondary"
              onClick={() => {
                setLookupDone(false);
                setLookupResults([]);
                setIsNewClient(true);
                setCurrentStep(2);
              }}
            >
              I&apos;m not listed — register me
            </button>
          </div>
        </div>
      );
    }

    // Default: phone entry
    return (
      <div className="kiosk-form-card">
        <h2 className="kiosk-section-title">Enter your phone number</h2>
        <p className="kiosk-section-description">
          We&apos;ll look up your account or get you set up quickly.
        </p>
        {renderError()}
        {isLoading ? (
          renderLoading()
        ) : (
          <>
            <div className="kiosk-form-group">
              <label htmlFor="fd-phone">Phone Number</label>
              <input
                id="fd-phone"
                type="tel"
                inputMode="numeric"
                placeholder="(305) 555-0100"
                value={phoneDisplay}
                onChange={(e) => setPhoneDisplay(formatPhone(e.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handlePhoneLookup();
                  }
                }}
                autoComplete="tel"
                autoFocus
              />
            </div>
            <div className="kiosk-nav-buttons">
              <button
                className="kiosk-button kiosk-button-primary"
                onClick={handlePhoneLookup}
                disabled={normalizePhone(phoneDisplay).length < 10}
              >
                Continue
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── Step 2: New Client Intake ────────────────────────────────────────────

  function renderStep2() {
    return (
      <div className="kiosk-form-card">
        <h2 className="kiosk-section-title">Tell us about yourself</h2>
        <p className="kiosk-section-description">
          This information helps us serve you and your family.
        </p>
        {renderError()}
        {isLoading ? (
          renderLoading()
        ) : (
          <>
            <div className="kiosk-form-row">
              <div className="kiosk-form-group">
                <label htmlFor="fd-first-name">
                  First Name <span className="required">*</span>
                </label>
                <input
                  id="fd-first-name"
                  type="text"
                  value={intakeFirstName}
                  onChange={(e) => setIntakeFirstName(e.target.value)}
                  autoCapitalize="words"
                  autoComplete="given-name"
                />
              </div>
              <div className="kiosk-form-group">
                <label htmlFor="fd-last-name">
                  Last Name <span className="required">*</span>
                </label>
                <input
                  id="fd-last-name"
                  type="text"
                  value={intakeLastName}
                  onChange={(e) => setIntakeLastName(e.target.value)}
                  autoCapitalize="words"
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="kiosk-form-group">
              <label htmlFor="fd-phone-display">Phone Number</label>
              <input
                id="fd-phone-display"
                type="tel"
                value={phoneDisplay}
                disabled
                style={{ background: '#f5f5f5', color: '#666' }}
              />
            </div>

            <div className="kiosk-form-group">
              <label htmlFor="fd-zip">
                Zip Code <span className="required">*</span>
              </label>
              <input
                id="fd-zip"
                type="text"
                inputMode="numeric"
                placeholder="33127"
                maxLength={5}
                value={intakeZip}
                onChange={(e) => setIntakeZip(e.target.value.replace(/\D/g, ''))}
              />
            </div>

            <div className="kiosk-form-group">
              <label>
                Household Size <span className="required">*</span>
              </label>
              <div className="fd-stepper">
                <button
                  type="button"
                  className="fd-stepper-button"
                  onClick={() => setHouseholdSize((v) => Math.max(1, v - 1))}
                  disabled={householdSize <= 1}
                  aria-label="Decrease household size"
                >
                  −
                </button>
                <span className="fd-stepper-value">{householdSize}</span>
                <button
                  type="button"
                  className="fd-stepper-button"
                  onClick={() => setHouseholdSize((v) => v + 1)}
                  aria-label="Increase household size"
                >
                  +
                </button>
              </div>
            </div>

            <div className="kiosk-form-group">
              <label>Children in Household</label>
              <div className="fd-stepper">
                <button
                  type="button"
                  className="fd-stepper-button"
                  onClick={() => setChildrenCount((v) => Math.max(0, v - 1))}
                  disabled={childrenCount <= 0}
                  aria-label="Decrease children count"
                >
                  −
                </button>
                <span className="fd-stepper-value">{childrenCount}</span>
                <button
                  type="button"
                  className="fd-stepper-button"
                  onClick={() => setChildrenCount((v) => v + 1)}
                  aria-label="Increase children count"
                >
                  +
                </button>
              </div>
            </div>

            {/* TEFAP Eligibility */}
            <div className="kiosk-form-group" style={{ marginTop: '0.5rem' }}>
              <label>
                TEFAP Eligibility <span className="required">*</span>
              </label>
              <p
                className="kiosk-section-description"
                style={{ fontSize: '0.85rem', margin: '0.25rem 0 0.75rem' }}
              >
                You are eligible to receive food from TEFAP if your household meets the income
                guidelines or participates in any of the following programs. Please select all that
                apply.
              </p>
              <div className="fd-needs-grid">
                {TEFAP_ELIGIBILITY_OPTIONS.map((opt) => (
                  <label
                    key={opt}
                    className="kiosk-checkbox"
                    style={
                      tefapSelections.has(opt)
                        ? { borderColor: '#5B2C86', background: 'rgba(91,44,133,0.07)' }
                        : {}
                    }
                  >
                    <input
                      type="checkbox"
                      checked={tefapSelections.has(opt)}
                      onChange={() => {
                        setTefapSelections((prev) => {
                          const next = new Set(prev);
                          if (next.has(opt)) {
                            next.delete(opt);
                          } else {
                            next.add(opt);
                          }
                          return next;
                        });
                      }}
                      style={{ accentColor: '#5B2C86' }}
                    />
                    <span>{opt}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="kiosk-nav-buttons">
              <button
                className="kiosk-button kiosk-button-secondary"
                onClick={() => {
                  setLookupDone(false);
                  setLookupResults([]);
                  setIsNewClient(false);
                  setCurrentStep(1);
                }}
              >
                Back
              </button>
              <button className="kiosk-button kiosk-button-primary" onClick={handleIntakeSubmit}>
                Continue
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── Step 3: Distribution Type ───────────────────────────────────────────

  function renderStep3() {
    // Returning client needs TEFAP — show TEFAP prompt before distribution type
    if (needsTefap) {
      return (
        <div className="kiosk-form-card">
          <h2 className="kiosk-section-title">TEFAP Eligibility</h2>
          <p className="kiosk-section-description">
            You are eligible to receive food from TEFAP if your household meets the income
            guidelines or participates in any of the following programs. Please select all that
            apply.
          </p>
          {renderError()}
          <div className="fd-needs-grid">
            {TEFAP_ELIGIBILITY_OPTIONS.map((opt) => (
              <label
                key={opt}
                className="kiosk-checkbox"
                style={
                  tefapSelections.has(opt)
                    ? { borderColor: '#5B2C86', background: 'rgba(91,44,133,0.07)' }
                    : {}
                }
              >
                <input
                  type="checkbox"
                  checked={tefapSelections.has(opt)}
                  onChange={() => {
                    setTefapSelections((prev) => {
                      const next = new Set(prev);
                      if (next.has(opt)) {
                        next.delete(opt);
                      } else {
                        next.add(opt);
                      }
                      return next;
                    });
                  }}
                  style={{ accentColor: '#5B2C86' }}
                />
                <span>{opt}</span>
              </label>
            ))}
          </div>
          <div className="kiosk-nav-buttons">
            <button
              className="kiosk-button kiosk-button-secondary"
              onClick={() => {
                setNeedsTefap(false);
                setCurrentStep(1);
                setLookupDone(false);
                setLookupResults([]);
              }}
            >
              Back
            </button>
            <button
              className="kiosk-button kiosk-button-primary"
              disabled={tefapSelections.size === 0}
              onClick={() => {
                setNeedsTefap(false);
                setErrorMsg('');
              }}
            >
              Continue
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="kiosk-form-card">
        <h2 className="kiosk-section-title">What can we help you with today?</h2>
        <p className="kiosk-section-description">Please select the service you&apos;re here for.</p>
        {renderError()}
        <div className="fd-distribution-grid">
          {DISTRIBUTION_TYPES.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`fd-distribution-option ${distributionType === opt.value ? 'selected' : ''}`}
              onClick={() => setDistributionType(opt.value)}
            >
              <input
                type="radio"
                checked={distributionType === opt.value}
                onChange={() => setDistributionType(opt.value)}
                aria-hidden="true"
                tabIndex={-1}
              />
              <span>{opt.label}</span>
            </button>
          ))}
        </div>
        <div className="kiosk-nav-buttons">
          <button
            className="kiosk-button kiosk-button-secondary"
            onClick={() => {
              if (isNewClient) {
                setCurrentStep(2);
              } else {
                setCurrentStep(1);
                setLookupDone(false);
                setLookupResults([]);
              }
            }}
          >
            Back
          </button>
          <button
            className="kiosk-button kiosk-button-primary"
            disabled={!distributionType}
            onClick={() => setCurrentStep(4)}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // ─── Step 4: USDA Survey ──────────────────────────────────────────────────

  function renderHhOptionCards(selected: UsdaHhAnswer, onSelect: (v: UsdaHhAnswer) => void) {
    return (
      <div className="fd-radio-options">
        {USDA_HH_OPTIONS.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`fd-radio-option ${selected === opt ? 'selected' : ''}`}
            onClick={() => onSelect(opt)}
          >
            <span>{opt}</span>
          </button>
        ))}
      </div>
    );
  }

  function renderYesNoCards(selected: UsdaYesNoAnswer, onSelect: (v: UsdaYesNoAnswer) => void) {
    return (
      <div className="fd-radio-options">
        {USDA_YES_NO_OPTIONS.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`fd-radio-option ${selected === opt ? 'selected' : ''}`}
            onClick={() => onSelect(opt)}
          >
            <span>{opt}</span>
          </button>
        ))}
      </div>
    );
  }

  function renderStep4() {
    const surveyComplete =
      usdaHh3 !== '' && usdaHh4 !== '' && usdaAd1 !== '' && usdaAd2 !== '' && usdaAd3 !== '';

    return (
      <div className="kiosk-form-card">
        <h2 className="kiosk-section-title">Food Security Survey</h2>
        <div className="fd-survey-intro">
          These questions are about the food eaten in your household in the last 30 days. Your
          answers are confidential and help us understand how to better serve you.
        </div>
        {renderError()}

        {/* HH3 */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div className="fd-question-row">
            <span className="fd-question-number">1</span>
            <p className="fd-question-text">
              {
                '"The food that I/we bought just didn\'t last, and I/we didn\'t have money to get more."'
              }
            </p>
          </div>
          {renderHhOptionCards(usdaHh3, setUsdaHh3)}
        </div>

        {/* HH4 */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div className="fd-question-row">
            <span className="fd-question-number">2</span>
            <p className="fd-question-text">{'"I/we couldn\'t afford to eat balanced meals."'}</p>
          </div>
          {renderHhOptionCards(usdaHh4, setUsdaHh4)}
        </div>

        {/* AD1 */}
        <div style={{ marginBottom: usdaAd1 === 'Yes' ? '0.5rem' : '1.5rem' }}>
          <div className="fd-question-row">
            <span className="fd-question-number">3</span>
            <p className="fd-question-text">
              {
                '"Did you or other adults ever cut the size of your meals or skip meals because there wasn\'t enough money for food?"'
              }
            </p>
          </div>
          {renderYesNoCards(usdaAd1, setUsdaAd1)}
        </div>

        {/* AD1a — conditional sub-question */}
        {usdaAd1 === 'Yes' && (
          <div className="fd-sub-question" style={{ marginBottom: '1.5rem' }}>
            <p className="fd-sub-label">How many days did this happen? (0–30)</p>
            <input
              type="number"
              inputMode="numeric"
              className="fd-days-input"
              min={0}
              max={30}
              value={usdaAd1aDontknow ? '' : usdaAd1aDays}
              disabled={usdaAd1aDontknow}
              placeholder="Enter days"
              onChange={(e) =>
                setUsdaAd1aDays(Math.min(30, Math.max(0, parseInt(e.target.value) || 0)))
              }
            />
            <label className="fd-dontknow-row">
              <input
                type="checkbox"
                checked={usdaAd1aDontknow}
                onChange={(e) => setUsdaAd1aDontknow(e.target.checked)}
              />
              <span>Don&apos;t know / prefer not to say</span>
            </label>
          </div>
        )}

        {/* AD2 */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div className="fd-question-row">
            <span className="fd-question-number">4</span>
            <p className="fd-question-text">
              {
                '"Did you ever eat less than you felt you should because there wasn\'t enough money for food?"'
              }
            </p>
          </div>
          {renderYesNoCards(usdaAd2, setUsdaAd2)}
        </div>

        {/* AD3 */}
        <div style={{ marginBottom: '1.5rem' }}>
          <div className="fd-question-row">
            <span className="fd-question-number">5</span>
            <p className="fd-question-text">
              {
                '"Were you ever hungry but didn\'t eat because there wasn\'t enough money for food?"'
              }
            </p>
          </div>
          {renderYesNoCards(usdaAd3, setUsdaAd3)}
        </div>

        <div className="kiosk-nav-buttons">
          <button className="kiosk-button kiosk-button-secondary" onClick={() => setCurrentStep(3)}>
            Back
          </button>
          <button
            className="kiosk-button kiosk-button-primary"
            disabled={!surveyComplete}
            onClick={() => setCurrentStep(5)}
          >
            Continue
          </button>
        </div>
      </div>
    );
  }

  // ─── Step 5: Fruit/Veg + Distribution ────────────────────────────────────

  function renderStep5() {
    return (
      <div className="kiosk-form-card">
        <h2 className="kiosk-section-title">Fruits & Vegetables</h2>
        <p className="kiosk-section-description">One more quick question.</p>
        {renderError()}

        {/* Fruit/veg slider */}
        <div style={{ marginBottom: '1.75rem' }}>
          <div className="fd-question-row">
            <p className="fd-question-text">
              In the last 7 days, on how many days did you eat at least 1 serving of fruits or
              vegetables?
            </p>
          </div>

          <div className={`fd-slider-wrap ${fruitvegDontknow ? 'disabled' : ''}`}>
            <div className="fd-slider-value">{fruitvegDays}</div>
            <div className="fd-slider-unit">{fruitvegDays === 1 ? 'day' : 'days'}</div>
            <input
              type="range"
              className="fd-slider"
              min={0}
              max={7}
              value={fruitvegDays}
              disabled={fruitvegDontknow}
              onChange={(e) => setFruitvegDays(Number(e.target.value))}
              aria-label="Days eating fruits or vegetables"
            />
            <div className="fd-slider-ticks">
              <span>0</span>
              <span>1</span>
              <span>2</span>
              <span>3</span>
              <span>4</span>
              <span>5</span>
              <span>6</span>
              <span>7</span>
            </div>
          </div>

          <label className="fd-dontknow-row">
            <input
              type="checkbox"
              checked={fruitvegDontknow}
              onChange={(e) => setFruitvegDontknow(e.target.checked)}
            />
            <span>Don&apos;t know / prefer not to say</span>
          </label>
        </div>

        <div className="kiosk-nav-buttons">
          <button className="kiosk-button kiosk-button-secondary" onClick={() => setCurrentStep(4)}>
            Back
          </button>
          <button
            className="kiosk-button kiosk-button-primary"
            disabled={isLoading}
            onClick={handleAfterStep5}
          >
            {isLoading ? 'Please wait...' : 'Continue'}
          </button>
        </div>
      </div>
    );
  }

  // ─── Step 6: Referral Prompt ──────────────────────────────────────────────

  function renderStep6() {
    function toggleReferral(r: string) {
      setSelectedReferrals((prev) => {
        const next = new Set(prev);
        if (next.has(r)) {
          next.delete(r);
        } else {
          next.add(r);
        }
        return next;
      });
    }

    return (
      <div className="kiosk-form-card">
        <h2 className="kiosk-section-title">Additional Support Available</h2>
        <div className="kiosk-alert kiosk-alert-info">
          Based on your responses, you may benefit from additional services. Would you like us to
          connect you with support?
        </div>
        {renderError()}
        <p className="kiosk-section-description">Select any services you&apos;d like help with:</p>
        <div className="fd-needs-grid">
          {REFERRAL_TYPE_OPTIONS.map((r) => (
            <label
              key={r}
              className="kiosk-checkbox"
              style={
                selectedReferrals.has(r)
                  ? { borderColor: '#5B2C86', background: 'rgba(91,44,133,0.07)' }
                  : {}
              }
            >
              <input
                type="checkbox"
                checked={selectedReferrals.has(r)}
                onChange={() => toggleReferral(r)}
                style={{ accentColor: '#5B2C86' }}
              />
              <span>{r}</span>
            </label>
          ))}
        </div>
        {isLoading ? (
          renderLoading()
        ) : (
          <div className="kiosk-nav-buttons">
            <button className="kiosk-button kiosk-button-secondary" onClick={() => submitVisit([])}>
              No thanks
            </button>
            <button
              className="kiosk-button kiosk-button-gold"
              onClick={() => submitVisit([...selectedReferrals])}
            >
              Yes, connect me
            </button>
          </div>
        )}
      </div>
    );
  }

  // ─── Step 7: Confirmation ─────────────────────────────────────────────────

  function renderStep7() {
    return (
      <div
        className="kiosk-success-page"
        style={{ minHeight: 'auto', flex: 1, borderRadius: '16px' }}
      >
        <div className="kiosk-success-icon" aria-hidden="true">
          ✓
        </div>
        <h2 className="kiosk-success-title">You&apos;re checked in!</h2>
        <p className="kiosk-success-message">Thank you for visiting Belafonte TACOLCY Center.</p>

        {visit && (
          <div className="kiosk-success-details">
            <p>Visit #{visit.visitSequence}</p>
            <p>Distribution: {visit.distributionType}</p>
          </div>
        )}

        {confirmedReferrals.length > 0 && (
          <div className="fd-confirmation-referrals">
            <p>A staff member will follow up with you about:</p>
            <ul>
              {confirmedReferrals.map((r) => (
                <li key={r}>{r}</li>
              ))}
            </ul>
          </div>
        )}

        <p className="kiosk-countdown">
          This screen will reset in {countdown} second{countdown !== 1 ? 's' : ''}
        </p>
        <button
          className="kiosk-button kiosk-button-primary"
          onClick={handleReset}
          style={{ marginTop: '1rem', minWidth: '200px' }}
        >
          Done
        </button>
      </div>
    );
  }

  // ─── Main render ──────────────────────────────────────────────────────────

  function renderCurrentStep() {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      case 5:
        return renderStep5();
      case 6:
        return renderStep6();
      case 7:
        return renderStep7();
      default:
        return renderStep1();
    }
  }

  return (
    <div className="kiosk-page">
      <div className="kiosk-container">
        <header className="kiosk-header">
          <img src={logo} alt="Belafonte TACOLCY Center" />
          <h1>Food Distribution Check-In</h1>
          <p>Belafonte TACOLCY Center</p>
        </header>

        {currentStep < 7 && renderProgress()}

        <main style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {renderCurrentStep()}
        </main>
      </div>
    </div>
  );
}

export default FoodCheckInPage;
